import clsx from "clsx";
import { TextInput } from "flowbite-react";
import * as React from "react";
import { INPUT_COLORS } from "@/enums/library";
import { GenericInputTypes, Status } from "@/types/common/library";
import {
  formatPhoneNumber,
  genericNumberPattern,
} from "@/utils/library/formatters";
import Button from "./Button";
import Icon, { ICON_SIZES, IconProps } from "./Icon";
import InputLabel from "./InputLabel";

type InputType =
  | React.HTMLInputTypeAttribute
  | "passwordShow"
  | "phone"
  | "url"
  | "ssn"
  | "user"
  | "odometer"
  | "vin"
  | "garage"
  | "address";

export type InputProps = {
  type: InputType;
  icon?: JSX.Element;
  tooltipText?: React.ReactNode;
} & GenericInputTypes;

const ICON_DEFAULTS: Pick<IconProps, "size" | "className"> = {
  size: ICON_SIZES.LG,
  className: "w-6",
};

type InputTypeConfig = (type: InputType) => {
  type: React.HTMLInputTypeAttribute;
  icon?: JSX.Element;
  pattern?: string;
};

const getInputTypeConfig: InputTypeConfig = (type) => {
  switch (type) {
    case "email":
      return {
        type,
        icon: <Icon icon="envelope" {...ICON_DEFAULTS} />,
      };
    case "url":
      return {
        type: "text",
        icon: <Icon icon="globe" {...ICON_DEFAULTS} />,
      };
    case "ssn":
      return {
        type: "number",
        icon: <Icon icon="user-vneck-hair" {...ICON_DEFAULTS} />,
      };
    case "user":
      return {
        type: "text",
        icon: <Icon icon="user-crown" {...ICON_DEFAULTS} />,
      };
    case "password":
      return {
        type,
        icon: <Icon icon="key" {...ICON_DEFAULTS} />,
      };
    case "passwordShow":
      return {
        type: "text",
        icon: <Icon icon="key" {...ICON_DEFAULTS} />,
      };
    case "odometer":
      return {
        type: "number",
        icon: <Icon icon="gauge-low" {...ICON_DEFAULTS} />,
      };
    case "vin":
      return {
        type: "text",
        icon: <Icon icon="car-side" {...ICON_DEFAULTS} />,
      };
    case "garage":
      return {
        type: "text",
        icon: <Icon icon="garage-car" {...ICON_DEFAULTS} />,
      };
    case "address":
      return {
        type: "text",
        icon: <Icon icon="house-chimney" {...ICON_DEFAULTS} />,
      };
    case "phone":
      return {
        type: "tel",
        icon: <Icon icon="phone" {...ICON_DEFAULTS} />,
      };
    case "numberGeneric":
      return {
        type: "tel",
        pattern: genericNumberPattern,
      };
    default:
      return {
        type,
      };
  }
};

const getInputColor = ({
  error,
  warning,
  success,
}: Status): INPUT_COLORS | undefined => {
  if (error) {
    return INPUT_COLORS.ERROR;
  }

  if (warning) {
    return INPUT_COLORS.WARNING;
  }

  if (success) {
    return INPUT_COLORS.SUCCESS;
  }

  return undefined;
};

const Input: React.FC<InputProps> = ({
  label,
  icon,
  success,
  error,
  warning,
  clearable = false,
  type = "text",
  clean = false,
  ["data-testid"]: dataTestId,
  className,
  tooltipText,
  ...otherProps
}) => {
  const [phoneVal, setPhoneVal] = React.useState("");
  const showRequiredLabel = !!label && !!otherProps.required;
  const {
    type: updatedType,
    icon: updatedIcon,
    pattern: updatedPattern,
  } = getInputTypeConfig(type);

  const phoneChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhoneNumber = formatPhoneNumber(e.target.value);
    setPhoneVal(formattedPhoneNumber);
    otherProps.onChange &&
      otherProps.onChange({
        ...e,
        target: {
          ...e.target,
          value: formattedPhoneNumber,
        },
      });
  };

  return (
    <>
      {label && (
        <InputLabel
          data-testid={dataTestId && `${dataTestId}_label`}
          htmlFor={otherProps.id}
          value={showRequiredLabel ? `${label} *` : label}
          tooltipText={tooltipText}
        />
      )}
      <TextInput
        type={updatedType}
        color={getInputColor({ error, warning, success })}
        data-testid={dataTestId}
        addon={clean ? undefined : icon || updatedIcon}
        pattern={updatedPattern}
        {...otherProps}
        className={clsx(
          "[&_input]:bg-white",
          "dark:[&_input]:bg-gray_comp",
          "dark:[&_input]:!ring-gray-600",
          "dark:[&_input]:!border-gray-600",
          className
        )}
        {...(type === "phone" && {
          onChange: phoneChangeHandler,
          value: otherProps.value || phoneVal,
        })}
      />
      {clearable && !!otherProps.onChange && (
        <div className={clsx("float-right", "mt-[-42px]", "relative")}>
          <Button
            type="none"
            onClick={() =>
              otherProps.onChange &&
              otherProps.onChange({
                currentTarget: {
                  value: "",
                },
              } as React.ChangeEvent<HTMLInputElement>)
            }
          >
            <Icon icon="x" />
          </Button>
        </div>
      )}
    </>
  );
};
export default Input;
